import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ControlledSelect, ControlledTextField } from '../../../../shared/components';
import { formatAmount } from '../../../../shared/utils/misc';
import {
  DEFAULT_MILESTONE_COUNT,
  DEFAULT_MILESTONES,
  PAYMENT_MILESTONE_COUNT_OPTIONS,
} from '../../../domain/work.constants';

export default function MilestonesSection({ grossTotal }) {
  const {
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useFormContext();

  const count = watch('milestones.count');
  const items = watch('milestones.items');

  const shouldShowTotalError = dirtyFields?.milestones?.items && (errors?.milestones as any)?.totalCheck;

  /**
   * Rebuild the rows when the number of milestones changes — but not on the first render.
   *
   * The table opened from a saved job arrives already filled in, and this effect used to
   * run on mount and overwrite it with blank rows. In the wizard that was invisible, since
   * what it wrote matched the defaults already there; against a job's stored milestones it
   * would have wiped them before they could be seen.
   */
  const lastCount = useRef(null);

  useEffect(() => {
    if (!count) return;

    const previous = lastCount.current;
    lastCount.current = count;

    if (previous === count) return;
    // First render: whatever is in the form already answers to this count, seeded or default.
    if (previous === null && Array.isArray(items) && items.length === count) return;

    // Back to the firm's standard four, wording and split included, when the count says
    // four; any other count is the user's own schedule, so those rows start blank.
    const defaults =
      count === DEFAULT_MILESTONE_COUNT
        ? DEFAULT_MILESTONES.map((milestone) => ({ ...milestone }))
        : Array.from({ length: count }, (_, i) => ({
            index: i + 1,
            percentage: '',
            label: '',
          }));

    setValue('milestones.items', defaults, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [count, items, setValue]);

  /**
   * The last milestone takes the balance — but only when one of the rows above it changes.
   *
   * It used to be read-only and filled only while it was the one empty box, so once every row
   * held a value (the standard 10/40/40/10 included) editing an earlier row left the total off
   * 100% with no way to correct the last one. It also refilled any box the moment it was
   * cleared, which made retyping a percentage impossible. Now the last box is editable, what
   * is typed into it stands, and the total check below reports a schedule that misses 100%.
   */
  const othersKey = (items ?? [])
    .slice(0, -1)
    .map((m) => (m?.percentage == null ? '' : String(m.percentage)))
    .join('|');
  const lastOthersKey = useRef<string | null>(null);

  useEffect(() => {
    const previous = lastOthersKey.current;
    lastOthersKey.current = othersKey;
    // First render, or only the last row changed: leave the last row as it is.
    if (previous === null || previous === othersKey) return;
    if (!items || items.length < 2) return;

    const others = items.slice(0, -1);
    if (others.some((m) => m?.percentage === '' || m?.percentage == null)) return;

    const remaining = 100 - others.reduce((sum, m) => sum + Number(m.percentage || 0), 0);
    if (remaining <= 0) return;

    setValue(`milestones.items.${items.length - 1}.percentage`, remaining, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [othersKey, items, setValue]);

  return (
    <Box className='column gap-3'>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '10rem auto',
          columnGap: 2,
          alignItems: 'center',
        }}
      >
        <Typography variant='body1' fontWeight={500}>
          4. Milestones
        </Typography>

        <ControlledSelect
          name='milestones.count'
          id='milestone-count'
          label='No. of Milestones'
          placeholder='Select number of milestones'
          options={PAYMENT_MILESTONE_COUNT_OPTIONS}
          showErrorInline
          sx={{ width: 180 }}
          orientation='row'
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 3fr auto',
          columnGap: 5,
          rowGap: 2,
          px: 4,
          width: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: '1/-1',
            color: 'grey.500',
            fontWeight: 500,
          }}
        >
          <span>No.</span>
          <span>Percentage</span>
          <span>Particulars</span>
          <span>Amount (incl. GST)</span>
        </Box>

        {items?.map((item, index) => {
          const amount =
            grossTotal && item.percentage
              ? (Number(grossTotal) * Number(item.percentage)) / 100
              : '';

          const label = `Milestone ${String(item.index).padStart(2, '0')}`;

          return (
            <Box
              key={item.index}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'subgrid',
                gridColumn: '1/-1',
                alignItems: 'center',
              }}
            >
              <div>
                <span>{label}</span>
                <div style={{ height: '20px' }} />
              </div>

              <ControlledTextField
                name={`milestones.items.${index}.percentage`}
                id={`milestone-percent-${index}`}
                inputMode='numeric'
                placeholder=''
                suffix='%'
                rules={{
                  validate: (v) => {
                    if (!v) return 'Required';
                    if (Number(v) <= 0) return 'Must be > 0';
                    if (Number(v) > 100) return 'Cannot exceed 100';
                    return true;
                  },
                }}
                sx={{ maxWidth: 100 }}
                showErrorInline
                reserveErrorSpace
              />

              <ControlledTextField
                name={`milestones.items.${index}.label`}
                id={`milestone-label-${index}`}
                placeholder='Specify milestone'
                rules={{ required: 'Required' }}
                fullWidth
                showErrorInline
                reserveErrorSpace
              />

              <div>
                <Typography textAlign='right'>{formatAmount(amount)}</Typography>
                <div style={{ height: '20px' }} />
              </div>
            </Box>
          );
        })}
      </Box>
      <Controller
        name='milestones.totalCheck'
        rules={{
          validate: () => {
            if (!items?.length) return true;

            const total = items.reduce((sum, m) => sum + Number(m?.percentage || 0), 0);

            return total === 100 || 'Milestone percentages must total 100%';
          },
        }}
        render={() => null}
      />

      {(errors?.milestones as any)?.totalCheck && (
        <Typography color='error' variant='body2'>
          {(errors.milestones as any).totalCheck.message}
        </Typography>
      )}
      {shouldShowTotalError && (
        <FormHelperText error>{(errors.milestones as any).totalCheck.message}</FormHelperText>
      )}
    </Box>
  );
}
